// input_controller
// отвечает только за базовую функциональность min_length, max_length

import { Controller } from '@hotwired/stimulus'
import { getRootFontSize } from '../utils/global'
import { createErrorHintElement } from '../utils/dom'

const PREFIX_MARGIN = 10
const INPUT_LEFT_PADDING = 18
const DEFAULT_FONT_SIZE = 16

// Пользовательская проверка формата: «что-то@что-то.домен» без пробелов.
// Сознательно проще RFC — отсекает опечатки, не отвергая валидные адреса.
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default class InputController extends Controller {
  static targets = ['input', 'errors', 'counter', 'prefix', 'error']
  static values = {
    minLength: Number,
    maxLength: Number,
    minMessage: String,
    maxMessage: String,
    requiredMessage: String,
    emailMessage: String
  }

  #errorHintElement = null

  initialize() {
    this.boundToggleEntered = this.toggleEntered.bind(this)
    this.boundSetFocused = this.setFocused.bind(this)
    this.boundUnsetFocused = this.unsetFocused.bind(this)
  }

  connect() {
    this.inputTarget.addEventListener('input', this.boundToggleEntered)
    this.inputTarget.addEventListener('focus', this.boundSetFocused)
    this.inputTarget.addEventListener('blur', this.boundUnsetFocused)
    // Проверяем начальное значение
    this.toggleEntered()
    this.updateTextareaWrapperState()
    // Обновляем счетчик для textarea
    this.updateCounter()
    // Вычисляем ширину префикса
    this.calculatePrefixWidth()
  }

  toggleEntered() {
    // Если поле НЕ пустое → добавляет класс is-entered (Показ кнопки сброса (reset))
    // Если поле пустое → убирает класс is-entered (Скрытие кнопки сброса (reset))
    this.element.classList.toggle(
      'is-entered',
      this.inputTarget.value.trim() !== ''
    )
  }

  disconnect() {
    if (this.boundToggleEntered) {
      this.inputTarget.removeEventListener('input', this.boundToggleEntered)
    }

    if (this.boundSetFocused) {
      this.inputTarget.removeEventListener('focus', this.boundSetFocused)
    }

    if (this.boundUnsetFocused) {
      this.inputTarget.removeEventListener('blur', this.boundUnsetFocused)
    }
  }

  validate() {
    const value = this.inputTarget.value.trim()
    const errors = []

    // Проверка обязательности
    if (this.inputTarget.required && value.length === 0) {
      errors.push(
        this.hasRequiredMessageValue
          ? this.requiredMessageValue
          : 'This field is required'
      )
    }

    // Проверка минимальной длины (только если есть значение)
    if (
      this.hasMinLengthValue &&
      value.length > 0 &&
      value.length < this.minLengthValue
    ) {
      errors.push(
        this.hasMinMessageValue
          ? this.minMessageValue.replace('$1', this.minLengthValue)
          : `Minimum ${this.minLengthValue} characters`
      )
    }

    // Проверка максимальной длины
    if (this.hasMaxLengthValue && value.length > this.maxLengthValue) {
      errors.push(
        this.hasMaxMessageValue
          ? this.maxMessageValue.replace('$1', this.maxLengthValue)
          : `Maximum ${this.maxLengthValue} characters`
      )
    }

    // Проверка формата email (только если есть значение)
    if (
      this.inputTarget.type === 'email' &&
      value.length > 0 &&
      !EMAIL_PATTERN.test(value)
    ) {
      errors.push(
        this.hasEmailMessageValue
          ? this.emailMessageValue
          : 'Enter a valid email address'
      )
    }

    this.showErrors(errors)
    this.updateCounter()

    return errors.length === 0
  }

  updateCounter() {
    if (this.hasCounterTarget) {
      const length = this.inputTarget.value.length
      const maxLength = this.hasMaxLengthValue ? this.maxLengthValue : 1000
      this.counterTarget.textContent = `${length}/${maxLength}`
    }
  }

  updateTextareaWrapperState() {
    const wrapper = this.inputTarget.closest('.textarea__wrapper')

    if (!wrapper) return

    wrapper.classList.toggle(
      'textarea__wrapper--has-counter',
      this.hasCounterTarget
    )
    wrapper.classList.toggle(
      'textarea__wrapper--has-label',
      Boolean(wrapper.querySelector('.textarea__label'))
    )
    wrapper.classList.toggle(
      'textarea__wrapper--disabled',
      this.inputTarget.disabled
    )
  }

  setFocused() {
    this.element.classList.add('is-focused')
  }

  unsetFocused() {
    this.element.classList.remove('is-focused')
  }

  showErrors(errors) {
    const hasErrors = errors.length > 0

    this.element.classList.toggle('is-invalid', hasErrors)

    if (this.#errorHintElement) {
      this.#errorHintElement.remove()
      this.#errorHintElement = null
    }

    // Серверная ошибка нарисована разметкой и своей жизни не имеет: раз поле
    // перепроверено, показывать её дальше не за что — значение уже другое.
    if (this.hasErrorTarget) {
      this.errorTarget.remove()
    }

    if (this.hasErrorsTarget && hasErrors) {
      this.#errorHintElement = createErrorHintElement(
        errors[0],
        this.element.classList.contains('input--no-inverse')
      )
      this.errorsTarget.appendChild(this.#errorHintElement)
    }
  }

  reset() {
    this.element.classList.remove('is-invalid')
    if (this.#errorHintElement) {
      this.#errorHintElement.remove()
      this.#errorHintElement = null
    }

    this.inputTarget.setCustomValidity('')
    this.updateCounter()
  }

  clearInput() {
    this.inputTarget.value = ''
    this.inputTarget.dispatchEvent(new Event('input', { bubbles: true }))
    this.inputTarget.focus()
    this.reset()
  }

  calculatePrefixWidth() {
    if (this.hasPrefixTarget) {
      const rootFontSize = getRootFontSize()
      const prefixWidthRem =
        this.prefixTarget.getBoundingClientRect().width / rootFontSize
      const basePaddingRem =
        (INPUT_LEFT_PADDING + PREFIX_MARGIN) / DEFAULT_FONT_SIZE
      const paddingRem = prefixWidthRem + basePaddingRem

      this.inputTarget.style.paddingLeft = `${paddingRem}rem`
    }
  }
}
